import { useState } from "react";
import type { ReactNode } from "react";
import {
  AccordionGroup,
  Chip,
  DisabledOptionRow,
  DisabledSectionBody,
  DisabledSlider,
  Field,
  MixedOptionRow,
  NumberField,
  OptionCard,
  SectionLabel,
  Select,
  ShapeIcon,
  Slider,
  Toggle,
} from "../../kit";
import { useCaps } from "../../editor/CapsProvider";
import { useRing } from "../ringContext";
import type {
  CenterStoneShape,
  DiamondOrigin,
  HeadUnderstructure,
  ProngCount,
  ProngTip,
  RingFit as EngRingFit,
  ShankStyle as EngShankStyle,
  StoneLayout,
} from "../../../shared/api-contract";

/**
 * The engagement-only tab. Shown only while the Engagement toggle is on (the
 * head assembly is attached). Everything the wedding band already owns —
 * profile, width, ring size, metal, melee run, engraving — lives in the shared
 * tabs; this one holds the centre stone, the head/setting and the two
 * engagement-only extras.
 *
 * Ported 1:1 from `ui/engagement/sections/{Diamonds,Head,More}Section.tsx`
 * (labels, ranges, steps and RPCs preserved) onto the merged `client.ringBuilder`
 * facade. Kit sliders draft-batch themselves, so no drag hooks are wired here.
 */

/** 10 selectable centre-stone cuts. NOTE: deliberately omits the two
 * `-elongated` variants that exist in `CenterStoneShape` — same set the old
 * engagement `DiamondsSection` exported (SpecBar looks labels up from it). */
export const SHAPES: { id: CenterStoneShape; label: string }[] = [
  { id: "round", label: "Round" },
  { id: "oval", label: "Oval" },
  { id: "princess", label: "Princess" },
  { id: "emerald", label: "Emerald" },
  { id: "cushion", label: "Cushion" },
  { id: "radiant", label: "Radiant" },
  { id: "pear", label: "Pear" },
  { id: "marquise", label: "Marquise" },
  { id: "asscher", label: "Asscher" },
  { id: "heart", label: "Heart" },
];

const ORIGINS: { id: DiamondOrigin; label: string }[] = [
  { id: "natural", label: "Natural" },
  { id: "lab", label: "Lab Grown" },
  { id: "skip", label: "Skip" },
];

const CARAT_MIN = 0.25;
const CARAT_MAX = 10;
const CARAT_STEP = 0.05;

const LAYOUTS: { id: StoneLayout; label: string }[] = [
  { id: "one", label: "One Stone" },
  { id: "two", label: "Two Stone" },
  { id: "three", label: "Three Stone" },
];

/** Flanking-stone shapes a centre stone is commonly paired with (subset of `SHAPES`). */
const SIDE_SHAPES: { id: CenterStoneShape; label: string }[] = [
  { id: "round", label: "Round" },
  { id: "oval", label: "Oval" },
  { id: "pear", label: "Pear" },
  { id: "princess", label: "Princess" },
  { id: "marquise", label: "Marquise" },
];

/** Side-stone size as a fraction of the centre stone. Three-stone sides read
 * smaller (accents); two-stone ("toi et moi") sides read near-equal. */
const SIDE_RATIO_RANGE: Record<"two" | "three", { min: number; max: number }> = {
  two: { min: 0.5, max: 1.0 },
  three: { min: 0.25, max: 0.5 },
};
const SIDE_RATIO_STEP = 0.05;

function clampSideRatio(layout: "two" | "three", ratio: number): number {
  const { min, max } = SIDE_RATIO_RANGE[layout];
  return Math.min(max, Math.max(min, ratio));
}

const UNDERSTRUCTURES: { id: HeadUnderstructure; label: string; live?: boolean; v56?: boolean }[] = [
  { id: "none", label: "None" },
  { id: "basket", label: "Basket" },
  { id: "halo", label: "Halo" },
  { id: "hidden-halo", label: "Hidden Halo" },
  { id: "bezel", label: "Bezel" },
  // Live as of W22: it used to ship its neighbour's geometry (60 changed pixels
  // out of 810,000) because there was no basket for the melee to sit in.
  { id: "pave-basket", label: "Pavé Basket" },
  { id: "double-halo", label: "Double Halo" },
  // v56, and ours — the reference has no cathedral basket either.
  { id: "cathedral-basket", label: "Cathedral Basket", v56: true },
];
const PRONG_COUNTS: { id: ProngCount; label: string }[] = [
  { id: "4-classic", label: "4 Classic" },
  { id: "4-compass", label: "4 Compass" },
  { id: "6", label: "6 Prong" },
  { id: "8", label: "8 Prong" },
];
const PRONG_TIPS: { id: ProngTip; label: string; v56?: boolean }[] = [
  { id: "claw", label: "Claw" },
  { id: "petite-claw", label: "Petite Claw" },
  { id: "rounded", label: "Rounded" },
  { id: "tab", label: "Tab" },
  { id: "double-claw", label: "Double Claw" },
  { id: "double-petite-claw", label: "Double Petite Claw" },
  // v56. The reference has no V tip at all; this is ours. It is also what every
  // square girdle and every pointed cut gets automatically, because the kit's
  // `WedgeTip` reaches 0.003 arm-u past its own strut and cannot hold a stone —
  // choosing it here puts a V on EVERY arm rather than only where the shape
  // needs one. See `prongTips.ts`.
  { id: "v", label: "V-Prong", v56: true },
];

/** Band rows that belong to the head, not to the band's cross-section. */
const SHANK_STYLES: { id: EngShankStyle; label: string }[] = [
  { id: "round", label: "Round" },
  { id: "square", label: "Square" },
  { id: "knife-edge", label: "Knife Edge" },
  { id: "split", label: "Split Shank" },
  { id: "twist", label: "Twist" },
];
const CATHEDRAL: { id: "none" | "cathedral"; label: string }[] = [
  { id: "none", label: "None" },
  { id: "cathedral", label: "Cathedral" },
];
const FITS: { id: EngRingFit; label: string }[] = [
  { id: "comfort", label: "Comfort Fit" },
  { id: "standard", label: "Standard Fit" },
];
const SURPRISE_STONE: { id: "off" | "on"; label: string }[] = [
  { id: "off", label: "None" },
  { id: "on", label: "Add Stone" },
];
const PRONG_PAVE: { id: "none" | "pave"; label: string }[] = [
  { id: "none", label: "None" },
  { id: "pave", label: "Pavé" },
];

const HALO_UNDERSTRUCTURES: HeadUnderstructure[] = ["halo", "hidden-halo", "double-halo"];

const HALO_STONE_MIN = 0.8;
const HALO_STONE_MAX = 1.5;
const BEZEL_WALL_MIN = 0.5;
const BEZEL_WALL_MAX = 1.0;

const COMPANION_BAND_WIDTH_MM = 2; // mirrors Viewer.COMPANION_BAND_WIDTH_MM
const COMPANION_BAND_GAP_MM = 0.15;

type GroupId = "diamonds" | "head" | "band" | "more";

const GROUPS: { id: GroupId; label: string; icon: string }[] = [
  { id: "diamonds", label: "Diamonds", icon: "diamond" },
  { id: "head", label: "Head", icon: "settings" },
  // W22. Shank Style, Cathedral and Fit are engagement-only rows that were
  // filed under "Profile" — a tab about the band's cross-section, three places
  // above a tab literally called "Engagement". All three were also DEAD until
  // W20 (the toggle wrote state and the band never rebuilt: 30 changed pixels
  // out of 810,000), which is how they sat unnoticed for two contract versions.
  // The reference keeps the same four rows in one Band panel; so do we now.
  { id: "band", label: "Band", icon: "radio_button_unchecked" },
  { id: "more", label: "More", icon: "tune" },
];

export function EngagementTab() {
  const { state } = useRing();
  const [open, setOpen] = useState<Set<GroupId>>(new Set(["diamonds", "head"]));

  const toggle = (id: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id as GroupId)) next.delete(id as GroupId);
      else next.add(id as GroupId);
      return next;
    });
  };

  // The shell only mounts this tab when engagement is on; guard anyway so a
  // stale render between the toggle and the state echo cannot throw.
  if (!state.engagement) return null;

  const body: Record<GroupId, ReactNode> = {
    diamonds: <DiamondsGroup />,
    head: <HeadGroup />,
    band: <BandGroup />,
    more: <MoreGroup />,
  };

  return (
    <div data-testid="ring-engagement-tab" className="flex flex-col">
      {GROUPS.map((g) => (
        <AccordionGroup key={g.id} id={g.id} label={g.label} icon={g.icon} open={open.has(g.id)} onToggle={toggle}>
          {body[g.id]}
        </AccordionGroup>
      ))}
    </div>
  );
}

// ─── Diamonds ────────────────────────────────────────────────────────────────

function DiamondsGroup() {
  const { state, r, apply } = useRing();
  const center = state.engagement!.centerStone;
  const sides = state.engagement!.sideStones;
  const [heartUnavailable, setHeartUnavailable] = useState(false);

  const selectLayout = (layout: StoneLayout) => {
    if (layout === "one") {
      void apply(r.setSideStones({ layout }));
      return;
    }
    void apply(r.setSideStones({ layout, caratRatio: clampSideRatio(layout, sides.caratRatio) }));
  };

  /** Heart is fully wired (own kit GLB + live catalog entry). Guard anyway: if
   * the viewer rejects the RPC, disable that one card instead of leaving the UI
   * stuck mid-click. */
  const selectCenterShape = async (shape: CenterStoneShape) => {
    try {
      await apply(r.setCenterStone({ shape }));
    } catch (err) {
      if (shape === "heart") {
        console.error("[EngagementTab] heart center stone unavailable; disabling", err);
        setHeartUnavailable(true);
      }
    }
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <SectionLabel>Center Stones</SectionLabel>
        <div className="grid grid-cols-3 gap-2">
          {LAYOUTS.map((l) => (
            <OptionCard key={l.id} selected={sides.layout === l.id} onClick={() => selectLayout(l.id)} title={l.label}>
              <LayoutIcon layout={l.id} />
              <span className="font-body text-[9px] uppercase tracking-[0.06em] text-on-surface-variant">
                {l.label}
              </span>
            </OptionCard>
          ))}
        </div>
      </div>

      {sides.layout !== "one" ? (
        <div className="flex flex-col gap-3">
          <Field label="Side Shape">
            <Select
              value={sides.shape}
              options={SIDE_SHAPES}
              variant="boxed"
              onChange={(v) => void apply(r.setSideStones({ shape: v as CenterStoneShape }))}
            />
          </Field>
          <Slider
            label="Side Stone Ratio"
            min={SIDE_RATIO_RANGE[sides.layout].min}
            max={SIDE_RATIO_RANGE[sides.layout].max}
            step={SIDE_RATIO_STEP}
            value={sides.caratRatio}
            display={(v) => `${v.toFixed(2)}× center`}
            onChange={(v) => void r.setSideStones({ caratRatio: v })}
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <SectionLabel>Shape</SectionLabel>
        <div className="grid grid-cols-3 gap-2">
          {SHAPES.map((s) => {
            const disabled = s.id === "heart" && heartUnavailable;
            return (
              <OptionCard
                key={s.id}
                selected={center.shape === s.id}
                onClick={() => { if (!disabled) void selectCenterShape(s.id); }}
                title={disabled ? "Currently unavailable" : s.label}
                className={disabled ? "pointer-events-none opacity-40" : ""}
              >
                <ShapeIcon shape={s.id} />
                <span className="font-body text-[9px] uppercase tracking-[0.06em] text-on-surface-variant">
                  {s.label}
                </span>
              </OptionCard>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <SectionLabel>Carat Weight</SectionLabel>
        <NumberField
          label="Center stone"
          min={CARAT_MIN}
          max={CARAT_MAX}
          step={CARAT_STEP}
          value={center.caratCt}
          format={(v) => v.toFixed(2)}
          onChange={(v) => void r.setCenterStone({ caratCt: v })}
        />
      </div>

      <div className="flex flex-col gap-2">
        <SectionLabel>Diamond Type</SectionLabel>
        <div className="flex gap-2">
          {ORIGINS.map((o) => (
            <Chip
              key={o.id}
              selected={center.origin === o.id}
              onClick={() => void apply(r.setCenterStone({ origin: o.id }))}
              className="flex-1"
            >
              {o.label}
            </Chip>
          ))}
        </div>
      </div>
    </>
  );
}

/** Dot glyph — one / two / three stones — matching `ShapeIcon`'s outline style. */
function LayoutIcon({ layout, size = 26 }: { layout: StoneLayout; size?: number }) {
  const common = { width: size, height: size * 0.6, viewBox: "0 0 24 14", fill: "none", stroke: "currentColor", strokeWidth: 1.2 };
  if (layout === "one") {
    return <svg {...common}><circle cx="12" cy="7" r="4.2" /></svg>;
  }
  if (layout === "two") {
    return (
      <svg {...common}>
        <circle cx="9" cy="7" r="3.6" />
        <circle cx="15.4" cy="7" r="3.6" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="4.5" cy="7" r="2.6" />
      <circle cx="12" cy="7" r="4" />
      <circle cx="19.5" cy="7" r="2.6" />
    </svg>
  );
}

// ─── Head ────────────────────────────────────────────────────────────────────

/** Understructure, prong count/tip/pavé and the halo/bezel dimension sliders.
 * A bezel has no prongs, so the prong rows render disabled with a hint while it
 * is selected. The two v56 values (Cathedral Basket, V-Prong) are hidden from an
 * older viewer, which would accept the RPC and then have nothing to build. */
function HeadGroup() {
  const { state, r, apply } = useRing();
  const caps = useCaps();
  const h = state.engagement!.head;
  const understructures = UNDERSTRUCTURES.filter((u) => !u.v56 || caps.ringHeadV56);
  const prongTips = PRONG_TIPS.filter((t) => !t.v56 || caps.ringHeadV56);

  const isHalo = HALO_UNDERSTRUCTURES.includes(h.understructure);
  const isBezel = h.understructure === "bezel";

  return (
    <div className="flex flex-col gap-5">
      <MixedOptionRow
        label="Basket & Halo"
        options={understructures}
        selected={h.understructure}
        onSelect={(id) => void apply(r.setHead({ understructure: id }))}
      />

      {isBezel ? (
        <div className="flex flex-col gap-2">
          <p className="font-body text-[11px] text-on-surface-variant">
            A bezel setting has no prongs — prong controls are unavailable.
          </p>
          <DisabledSectionBody>
            <DisabledOptionRow
              label="Prong Count"
              options={PRONG_COUNTS.map((x) => x.label)}
              selectedIndex={PRONG_COUNTS.findIndex((x) => x.id === h.prongCount)}
            />
            <DisabledOptionRow
              label="Prong Tips"
              options={prongTips.map((x) => x.label)}
              selectedIndex={prongTips.findIndex((x) => x.id === h.prongTip)}
            />
            <DisabledOptionRow
              label="Prong Pavé"
              options={PRONG_PAVE.map((x) => x.label)}
              selectedIndex={h.prongPave ? 1 : 0}
            />
          </DisabledSectionBody>
        </div>
      ) : (
        <>
          <MixedOptionRow
            label="Prong Count"
            options={PRONG_COUNTS}
            selected={h.prongCount}
            onSelect={(id) => void apply(r.setHead({ prongCount: id }))}
          />
          <MixedOptionRow
            label="Prong Tips"
            options={prongTips}
            selected={h.prongTip}
            onSelect={(id) => void apply(r.setHead({ prongTip: id }))}
          />
          <MixedOptionRow
            label="Prong Pavé"
            options={PRONG_PAVE}
            selected={h.prongPave ? "pave" : "none"}
            onSelect={(id) => void apply(r.setHead({ prongPave: id === "pave" }))}
          />
        </>
      )}

      {isHalo ? (
        <Slider
          label="Halo Stone Size"
          min={HALO_STONE_MIN}
          max={HALO_STONE_MAX}
          step={0.05}
          value={h.haloStoneSizeMM}
          unit="mm"
          onChange={(v) => void r.setHead({ haloStoneSizeMM: v })}
        />
      ) : (
        <DisabledSectionBody>
          <DisabledSlider label="Halo Stone Size" value={`${h.haloStoneSizeMM.toFixed(2)} mm`} />
        </DisabledSectionBody>
      )}

      {isBezel ? (
        <Slider
          label="Bezel Wall"
          min={BEZEL_WALL_MIN}
          max={BEZEL_WALL_MAX}
          step={0.05}
          value={h.bezelWallMM}
          unit="mm"
          onChange={(v) => void r.setHead({ bezelWallMM: v })}
        />
      ) : (
        <DisabledSectionBody>
          <DisabledSlider label="Bezel Wall" value={`${h.bezelWallMM.toFixed(2)} mm`} />
        </DisabledSectionBody>
      )}
    </div>
  );
}

// ─── More (engagement-only extras) ───────────────────────────────────────────

/**
 * Ring Builder › Engagement › Band — the shank rows that only exist when a head
 * is attached: silhouette, cathedral shoulders, inner fit and width.
 *
 * They used to live on the Profile tab, gated on `engagementOn` — so they were
 * invisible without a head and mis-filed with one. Moved here rather than
 * duplicated: two live copies of one control is how a dead one hides.
 */
function BandGroup() {
  const { state, r, apply } = useRing();
  const eng = state.engagement;
  if (!eng) return null;
  return (
    <div className="flex flex-col gap-5">
      <MixedOptionRow
        label="Shank Style"
        options={SHANK_STYLES}
        selected={eng.style ?? "round"}
        onSelect={(id) => void apply(r.setEngagement({ style: id }))}
      />
      <MixedOptionRow
        label="Cathedral"
        options={CATHEDRAL}
        selected={eng.cathedral ? "cathedral" : "none"}
        onSelect={(id) => void apply(r.setEngagement({ cathedral: id === "cathedral" }))}
      />
      <MixedOptionRow
        label="Fit"
        options={FITS}
        selected={eng.fit ?? "comfort"}
        onSelect={(id) => void apply(r.setEngagement({ fit: id }))}
      />
      <Slider
        label="Band Width"
        min={1.5}
        max={4}
        step={0.05}
        unit="mm"
        value={state.widthMM}
        onChange={(v: number) => void apply(r.setWidth(v))}
      />
    </div>
  );
}

/** "Complete the set" (a matching wedding band stacked beside the ring) and the
 * bridge-accent (surprise stone) row. Engraving text/font are NOT here — the merged
 * builder owns engraving once, in the Engrave tab. */
function MoreGroup() {
  const { state, r, apply } = useRing();
  const caps = useCaps();

  // The companion band lives entirely viewer-side (it is not part of RingState),
  // so the toggle + offset are tracked locally. The default offset mirrors the
  // viewer: half the shank width + half the band width + the air gap.
  const flushOffset = state.widthMM / 2 + COMPANION_BAND_WIDTH_MM / 2 + COMPANION_BAND_GAP_MM;
  const [companion, setCompanion] = useState(false);
  const [offset, setOffset] = useState(flushOffset);

  const onCompanion = (on: boolean) => {
    setCompanion(on);
    if (on) {
      setOffset(flushOffset);
      void apply(r.addCompanionBand());
    } else {
      void r.removeCompanionBand();
    }
  };

  const onOffset = (v: number) => {
    setOffset(v);
    void r.setCompanionBandLayout(v);
  };

  return (
    <div className="flex flex-col gap-5">
      {caps.companionBandV36 ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <SectionLabel>Complete the Set</SectionLabel>
            <Toggle checked={companion} onChange={onCompanion} label="Add wedding band" />
          </div>
          {companion ? (
            <NumberField label="Band spacing (mm)" min={1.5} max={6} step={0.05} value={offset} onChange={onOffset} />
          ) : null}
        </div>
      ) : null}

      {/*
        W22: this was `DisabledOptionRow`, which by construction has no click
        handler — yet the whole lane behind it was finished and shipping:
        `HeadAssembly._buildSurprise` seats the stone on the open gallery,
        disables it for a bezel, skips it mid-draft, disposes it properly and
        tallies it into the spec carat, and `setEngagementExtras` was wired
        through `ringBuilderApi`. The only missing piece was a control.
      */}
      <MixedOptionRow
        label="Bridge Accent"
        options={SURPRISE_STONE}
        selected={state.engagement!.extras.surpriseStones.enabled ? "on" : "off"}
        onSelect={(id) =>
          void apply(
            r.setEngagementExtras({
              surpriseStones: { ...state.engagement!.extras.surpriseStones, enabled: id === "on" },
            }),
          )
        }
      />
    </div>
  );
}
